import {
    createDisposer,
    getNetworkState,
    globalRef,
    type Maybe,
    onLine,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { data, type ObservableValue } from '@amazon/vinyl-observable'

/**
 * @param props Callbacks for when the onLine status has changed. Will be invoked once immediately.
 */
export function onOnlineChange(props: {
    readonly onOffline?: Maybe<() => void>
    readonly onOnline?: Maybe<() => void>
}): Unsubscribe {
    const { add, dispose } = createDisposer()
    const network = getNetworkState()
    if (onLine()) props.onOnline?.()
    else props.onOffline?.()
    if (props.onOnline) add(network.on('online', props.onOnline))
    if (props.onOffline) add(network.on('offline', props.onOffline))
    return dispose
}

// A global so it follows the network state the registry holds now, not one since reset and disposed.
const online = globalRef((): ObservableValue<boolean> => {
    const network = getNetworkState()
    const v = data(onLine())
    const refresh = () => (v.value = onLine())
    network.on('online', refresh)
    network.on('offline', refresh)
    return v
})

export const isOnline = (): ObservableValue<boolean> => online.value

export const isNotOnline = () => isOnline().map((v) => !v)

/** Calls `callback` once each time the network comes back after being offline. */
export function onReconnect(callback: () => void): Unsubscribe {
    return isOnline().onData((online, previous) => {
        if (online && previous === false) callback()
    })
}
